// ============================================================
// TWIN REPLAY — Fase 10.15 + Fase 11 (Hardened)
// Replay temporal do Digital Twin via snapshots
// FIX: state_snapshot→state, created_at→taken_at, AbortController
// ============================================================
import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { TEXT, SURFACE } from '@/design-system/tokens'
import type { TwinSnapshot } from '@/types/twin'

interface ReplayState {
  snapshots: TwinSnapshot[]
  current:   number
  playing:   boolean
  speedMs:   number
  isLoading: boolean
  error:     string | null
}

interface TwinReplayProps { obraId: string }

// ─── Snapshot data shape stored in DB ────────────────────
interface SnapshotData {
  zones?:      Array<{ id: string; nome: string; risk_level: string }>
  equipment?:  Array<{ id: string; nome: string; status: string }>
  risks?:      Array<{ is_active: boolean }>
  risk_count?: number
}

export function TwinReplay({ obraId }: TwinReplayProps) {
  const [state, setState] = useState<ReplayState>({
    snapshots: [],
    current:   0,
    playing:   false,
    speedMs:   1500,
    isLoading: false,
    error:     null,
  })

  const [daysBack, setDaysBack] = useState(1)
  const abortRef = useRef<AbortController | null>(null)

  // ─── Load snapshots with AbortController (FIX PROB-09) ──
  useEffect(() => {
    abortRef.current?.abort()
    abortRef.current = new AbortController()
    loadSnapshots(abortRef.current.signal)

    return () => { abortRef.current?.abort() }
  }, [obraId, daysBack])

  async function loadSnapshots(signal: AbortSignal): Promise<void> {
    setState(s => ({ ...s, isLoading: true, error: null }))

    const from = new Date(Date.now() - daysBack * 86_400_000).toISOString()

    const { data, error } = await supabase
      .from('twin_snapshots')
      .select('*')
      .eq('obra_id', obraId)
      .gte('taken_at', from)          // FIX: taken_at (DB column)
      .order('taken_at', { ascending: true })
      .limit(200)

    if (signal.aborted) return         // FIX: discard stale result

    if (error) {
      setState(s => ({ ...s, isLoading: false, error: error.message }))
      return
    }

    setState(s => ({
      ...s,
      snapshots: (data ?? []) as TwinSnapshot[],
      current:   0,
      playing:   false,
      isLoading: false,
    }))
  }

  // ─── Playback ───────────────────────────────────────────
  useEffect(() => {
    if (!state.playing || state.snapshots.length === 0) return

    const timer = setInterval(() => {
      setState(s => {
        if (s.current >= s.snapshots.length - 1) return { ...s, playing: false }
        return { ...s, current: s.current + 1 }
      })
    }, state.speedMs)

    return () => clearInterval(timer)
  }, [state.playing, state.speedMs, state.snapshots.length])

  const snap  = state.snapshots[state.current] ?? null
  const total = state.snapshots.length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT.primary }}>⏮ Twin Replay</div>
          <div style={{ fontSize: 11, color: TEXT.muted }}>{total} snapshots carregados</div>
        </div>
        <select
          value={daysBack}
          onChange={e => setDaysBack(Number(e.target.value))}
          style={{
            background: SURFACE.card, color: TEXT.primary,
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 6, padding: '4px 8px', fontSize: 12,
          }}
        >
          {[1, 3, 7, 14, 30].map(d => (
            <option key={d} value={d}>Últimas {d === 1 ? '24h' : `${d} dias`}</option>
          ))}
        </select>
      </div>

      {state.isLoading ? (
        <div style={{ textAlign: 'center', padding: 32, color: TEXT.muted }}>
          Carregando snapshots…
        </div>
      ) : state.error ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#ef4444', fontSize: 12 }}>
          Erro ao carregar: {state.error}
        </div>
      ) : total === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: TEXT.muted }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>📷</div>
          <div>Nenhum snapshot disponível para este período</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>
            Snapshots são gerados automaticamente a cada 5 minutos
          </div>
        </div>
      ) : (
        <>
          {snap && <SnapshotViewer snapshot={snap} index={state.current} total={total} />}

          {/* Timeline scrubber */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              type="range"
              min={0}
              max={Math.max(0, total - 1)}
              value={state.current}
              onChange={e => setState(s => ({ ...s, current: Number(e.target.value), playing: false }))}
              style={{ width: '100%', accentColor: '#3b82f6' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: TEXT.muted }}>
              <span>{state.snapshots[0] ? fmtTime(state.snapshots[0].taken_at) : ''}</span>
              <span>{state.snapshots[total - 1] ? fmtTime(state.snapshots[total - 1]!.taken_at) : ''}</span>
            </div>
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setState(s => ({ ...s, current: 0, playing: false }))}
              style={btnStyle}>⏮</button>
            <button onClick={() => setState(s => ({ ...s, current: Math.max(0, s.current - 1), playing: false }))}
              style={btnStyle}>◀</button>
            <button
              onClick={() => setState(s => ({ ...s, playing: !s.playing }))}
              style={{ ...btnStyle, background: 'rgba(59,130,246,0.2)', color: '#3b82f6', fontWeight: 700, padding: '6px 20px' }}
            >
              {state.playing ? '⏸ Pausar' : '▶ Play'}
            </button>
            <button onClick={() => setState(s => ({ ...s, current: Math.min(total - 1, s.current + 1), playing: false }))}
              style={btnStyle}>▶</button>
            <button onClick={() => setState(s => ({ ...s, current: total - 1, playing: false }))}
              style={btnStyle}>⏭</button>

            <select
              value={state.speedMs}
              onChange={e => setState(s => ({ ...s, speedMs: Number(e.target.value) }))}
              style={{ ...btnStyle, padding: '4px 8px', marginLeft: 'auto' }}
            >
              <option value={3000}>0.5×</option>
              <option value={1500}>1×</option>
              <option value={750}>2×</option>
              <option value={300}>5×</option>
            </select>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Snapshot Viewer ──────────────────────────────────────
function SnapshotViewer({ snapshot, index, total }: {
  snapshot: TwinSnapshot; index: number; total: number
}) {
  // FIX: use 'state' not 'state_snapshot' — aligned with DB column and TwinSnapshot type
  const data     = (snapshot.state ?? {}) as SnapshotData
  const zones    = data.zones    ?? []
  const risks    = data.risks    ?? []
  const critical = zones.filter(z => z.risk_level === 'critical' || z.risk_level === 'emergency').length

  return (
    <motion.div
      key={snapshot.id}
      initial={{ opacity: 0.7 }}
      animate={{ opacity: 1 }}
      style={{
        padding:      14,
        background:   SURFACE.card,
        border:       '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT.primary }}>
            Snapshot {index + 1}/{total}
          </div>
          <div style={{ fontSize: 11, color: TEXT.muted }}>
            {new Date(snapshot.taken_at).toLocaleString('pt-BR')}  {/* FIX: taken_at */}
            {snapshot.trigger && (
              <span style={{ marginLeft: 8 }}>· trigger: {snapshot.trigger}</span>
            )}
          </div>
        </div>
        {snapshot.risk_score !== undefined && (
          <div style={{
            fontSize:   20,
            fontWeight: 800,
            color:      snapshot.risk_score >= 70 ? '#ef4444'
                      : snapshot.risk_score >= 45 ? '#f97316'
                      : snapshot.risk_score >= 20 ? '#f59e0b' : '#10b981',
          }}>
            {Math.round(snapshot.risk_score)}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {[
          { label: 'Zonas',    value: zones.length },
          { label: 'Críticas', value: critical, warn: critical > 0 },
          { label: 'Riscos',   value: data.risk_count ?? risks.filter(r => r.is_active).length },
        ].map(m => (
          <div key={m.label} style={{
            textAlign: 'center', padding: '8px',
            background: 'rgba(255,255,255,0.04)', borderRadius: 6,
          }}>
            <div style={{
              fontSize:   18,
              fontWeight: 700,
              color:      'warn' in m && m.warn ? '#ef4444' : TEXT.primary,
            }}>
              {m.value}
            </div>
            <div style={{ fontSize: 10, color: TEXT.muted }}>{m.label}</div>
          </div>
        ))}
      </div>
    </motion.div>
  )
}

// ─── Helpers ──────────────────────────────────────────────
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const btnStyle: React.CSSProperties = {
  background:   'rgba(255,255,255,0.06)',
  border:       '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  color:        TEXT.primary,
  fontSize:     14,
  padding:      '5px 10px',
  cursor:       'pointer',
}
